import { FeedbackMessage } from 'components/secondaryMarket/shared/FeedbackMessage';
import type { TagItem } from 'components/shared/Tags/SgTagsPicker';
import { SgTagsPicker } from 'components/shared/Tags/SgTagsPicker';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { uniqBy } from 'lodash-es';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

export type DateSelectorProps = Readonly<{
  onValueChanged: (value: string) => void;
  availableValues: ReadonlyArray<string>;
  selectedValue: string;
  isDisabled: boolean;
  id: string;
  e2e?: string;
  placeholder: IntlKey;
  missingValueWarning?: IntlKey;
}>;

export const DateSelector: FC<DateSelectorProps> = ({
  onValueChanged,
  availableValues,
  selectedValue,
  isDisabled,
  id,
  e2e,
  placeholder,
  missingValueWarning,
}) => {
  const { formatDisplayedDate, formatMessage } = useSPIntl();
  const onValuationDateSelected = useCallback(
    (selection: readonly string[]) => {
      if (selection?.length === 1) {
        onValueChanged(selection[0]);
      }
    },
    [onValueChanged],
  );
  const availableValuationDatesItems = useMemo(
    () =>
      uniqBy(
        availableValues.concat(selectedValue)?.map(
          date =>
            ({
              key: date,
              label: formatDisplayedDate(date),
            }) as TagItem,
        ),
        'key',
      ) ?? [],
    [availableValues, formatDisplayedDate, selectedValue],
  );
  const placeholderText = formatMessage({ id: placeholder });
  const isSelectedValueMissing = missingValueWarning && !availableValues.includes(selectedValue);
  return (
    <>
      <SgTagsPicker
        id={id}
        available={availableValuationDatesItems}
        maxDisplayedItems={availableValuationDatesItems.length}
        singleSelect
        selection={[selectedValue]}
        onSelectionKeysChanged={onValuationDateSelected}
        disabled={isDisabled}
        e2e={e2e}
        placeholder={placeholderText}
        noIcon
        noClear
      />
      {isSelectedValueMissing && missingValueWarning !== undefined && (
        <FeedbackMessage labelKey={missingValueWarning} color="info" />
      )}
    </>
  );
};

DateSelector.displayName = 'ValuationDateSelector';
